import React, { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { LoadableAfterPaint } from '@confluence/loadable';

import { isAdfDocEmpty } from './admin-controls/utils/isAdfDocEmpty.ts';
import { parseAdfString } from './admin-controls/utils/parseAdfString.ts';

interface AccessDisabledMessageProps {
	messageContent: string | null | undefined;
	fallback: React.ReactNode;
}

// AFTER_PAINT is the only valid priority: it must be <= AFTER_PAINT to render during SSR, and must
// not outrank its loadable ancestors RestrictedSpace and RestrictedPage, both AFTER_PAINT.
// See go/cc-loading-phases.
const AdfMessageRendererLoader = LoadableAfterPaint<{ adfDoc: DocNode }>({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-AdfMessageRenderer" */ './AdfMessageRenderer.tsx'))
			.AdfMessageRenderer,
});

/**
 * Renders the admin-configured ADF message for an access-requests-disabled screen. The empty check
 * matters because the editor saves a cleared message as one empty paragraph rather than null.
 */
export const AccessDisabledMessage = ({
	messageContent,
	fallback,
}: AccessDisabledMessageProps): React.JSX.Element => {
	// Memoised so re-renders of the host screen don't re-parse the stored ADF, and so `adfDoc` keeps
	// a stable identity as a prop for the lazily-loaded renderer.
	const adfDoc: DocNode | undefined = useMemo(
		() => parseAdfString(messageContent),
		[messageContent],
	);

	if (!adfDoc || isAdfDocEmpty(adfDoc)) {
		return <>{fallback}</>;
	}

	return (
		<GenericErrorBoundary
			attribution={Attribution.PERMISSIONS_EXPERIENCE}
			renderOnError={() => <>{fallback}</>}
		>
			<AdfMessageRendererLoader adfDoc={adfDoc} />
		</GenericErrorBoundary>
	);
};
